import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS } from "./api/apiConfig";
import axiosInstance from "./axiosInstance";

export interface UserData {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role: string;
  phone?: string;
  avatar?: string;
  username?: string;
  title?: string;
  specialty?: string;
  dateOfBirth?: string | null;
  gender?: string;
  address?: string;
}

interface JwtPayload {
  exp: number;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const fetchUserProfile = async (id: string): Promise<UserData> => {
  if (!id || id === "undefined") {
    throw new Error("Invalid or undefined user ID");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("No authentication token found");
  }

  if (isTokenExpired(token)) {
    localStorage.removeItem("token");
    throw new Error("Authentication token has expired");
  }

  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.USERS.GET_BY_ID(parseInt(id, 10))
    );

    const userData: UserData = {
      ...response.data,
      id,
    };

    const userLabel =
      userData.fullName ||
      (userData.firstName && userData.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : null) ||
      userData.username ||
      "Utilisateur Inconnu";

    const userEmail = userData.email;
    if (!userEmail) {
      console.warn("No email found for user. Consider updating profile.");
    }

    localStorage.setItem("userLabel", userLabel);
    if (userEmail) {
      localStorage.setItem("userEmail", userEmail);
    }

    return userData;
  } catch (error: unknown) {
    console.error("Error fetching user profile:", error);
    if (
      error &&
      typeof error === "object" &&
      "isAxiosError" in error &&
      (error as any).isAxiosError
    ) {
      const axiosError = error as any;
      if (axiosError.response?.status === 403) {
        throw new Error("Unauthorized access to user profile");
      } else if (axiosError.response?.status === 401) {
        throw new Error("Authentication required");
      }
      throw new Error(axiosError.message || "Failed to fetch user profile");
    }
    throw new Error("An unexpected error occurred while fetching user profile");
  }
};
